import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';
import { customerService, type Customer } from '@/services/customer.service';
import { formatCustomerLabel } from '@/lib/customer-label';
import { useDebounce } from '@/hooks/use-debounce';

/**
 * Search-first customer picker for the intake page (Phase 1).
 *
 * Server-side, debounced typeahead over `GET /customers?search` — searches
 * name / customer code / email / phone / GST / contact. Never loads the full
 * customer list into the browser. Mirrors {@link EquipmentSearchCombobox}.
 *
 * When no match is found, the operator can open inline customer creation via
 * `onCreateNew` (the parent owns the modal). Selecting a result reports the
 * full {@link Customer} so the parent can display its label and drive the
 * dependent location/contact selectors.
 */
export function CustomerSearchSelect({
  selected,
  onSelect,
  onCreateNew,
  disabled,
}: {
  selected: Customer | null;
  onSelect: (customer: Customer | null) => void;
  onCreateNew: () => void;
  disabled?: boolean;
}) {
  const inputId = useId();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const debounced = useDebounce(term, 300);
  const containerRef = useRef<HTMLDivElement>(null);

  // Only query while the operator is actively searching with a term.
  const { data, isFetching } = useQuery({
    queryKey: ['customer-search', debounced],
    queryFn: () => customerService.list({ search: debounced, pageSize: 20 }),
    enabled: open && debounced.trim().length > 0,
  });

  // Close the dropdown on outside click.
  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const commit = (c: Customer) => {
    onSelect(c);
    setTerm('');
    setOpen(false);
  };

  const clear = () => {
    onSelect(null);
    setTerm('');
    setOpen(false);
  };

  const results = data?.items ?? [];

  // A customer is already selected — show it with a clear affordance.
  if (selected) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
        <span className="truncate text-sm font-medium">{formatCustomerLabel(selected)}</span>
        <button
          type="button"
          onClick={clear}
          disabled={disabled}
          aria-label="Clear customer selection"
          className="text-muted-foreground hover:text-foreground disabled:opacity-50"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <FontAwesomeIcon
          icon={faSearch}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          id={inputId}
          type="text"
          value={term}
          disabled={disabled}
          placeholder="Search by name, code, email, phone, or GST…"
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-9 text-sm outline-none focus:border-primary disabled:opacity-50"
        />
        {isFetching && (
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
        )}
      </div>

      {open && debounced.trim().length > 0 && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg">
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => commit(c)}
              className="block w-full truncate px-3 py-2 text-left text-sm hover:bg-accent"
            >
              {formatCustomerLabel(c)}
            </button>
          ))}
          {results.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {isFetching ? 'Searching…' : 'No customer found.'}
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onCreateNew();
            }}
            disabled={disabled}
            className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm font-medium text-primary hover:bg-accent disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faPlus} />
            Create new customer
          </button>
        </div>
      )}
    </div>
  );
}
